import { useState, useEffect } from "react";
import { useI18n } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, User, Globe2, StickyNote, Pencil, Trash2, BookMarked } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  character: { icon: User, color: "text-gold" },
  world: { icon: Globe2, color: "text-blue-400" },
  note: { icon: StickyNote, color: "text-emerald-400" },
};

const EMPTY = { type: "character", name: "", role: "", traits: "", relationships: "", notes: "" };

export default function BookBible({ projectId }) {
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/bible`);
      setEntries(data);
    } catch { setEntries([]); }
  };
  useEffect(() => { load(); }, [projectId]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ ...EMPTY, ...e }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/bible/${editing.id}`, form);
      } else {
        await api.post(`/projects/${projectId}/bible`, form);
      }
      setOpen(false);
      toast.success(t("toast.saved"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/bible/${id}`);
    load();
  };

  const filtered = filter === "all" ? entries : entries.filter((e) => e.type === filter);

  return (
    <div className="h-screen overflow-y-auto">
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-100 flex items-center gap-2.5"><BookMarked className="w-6 h-6 text-gold" /> {t("studio.bible")}</h1>
          </div>
          <Button onClick={openNew} className="bg-gold hover:bg-gold-light text-ink-900 font-semibold rounded-full h-10 px-5" data-testid="book-bible-add-character">
            <Plus className="w-4 h-4 mr-1.5" /> {t("bible.add")}
          </Button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {["all", "character", "world", "note"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`bible-filter-${f}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                filter === f ? "bg-gold text-ink-900" : "bg-ink-800 text-slate-400 hover:text-slate-200 border border-white/10"
              }`}
            >
              {f === "all" ? t("bible.all") : t(`bible.type.${f}`)}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="card-surface rounded-2xl p-12 text-center text-slate-400 text-sm" data-testid="bible-empty">
            {t("bible.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((e) => {
              const meta = TYPE_META[e.type] || TYPE_META.note;
              const Icon = meta.icon;
              return (
                <div key={e.id} className="card-surface rounded-xl p-5 group relative fade-up" data-testid={`bible-card-${e.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 ${meta.color}`}>
                      <Icon className="w-4.5 h-4.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-lg font-semibold text-slate-100 truncate">{e.name}</h3>
                      {e.role && <p className="text-xs text-gold/70">{e.role}</p>}
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(e)} className="text-slate-500 hover:text-gold" data-testid={`bible-edit-${e.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(e.id)} className="text-slate-500 hover:text-red-400" data-testid={`bible-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                  {e.traits && <p className="mt-3 text-xs text-slate-400"><span className="text-slate-500">{t("bible.traits")}: </span>{e.traits}</p>}
                  {e.relationships && <p className="mt-1.5 text-xs text-slate-400"><span className="text-slate-500">{t("bible.relationships")}: </span>{e.relationships}</p>}
                  {e.notes && <p className="mt-2 text-xs text-slate-300 leading-relaxed line-clamp-4">{e.notes}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-ink-800 border-white/10 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("bible.add")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3.5 mt-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.type.character")}</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-ink-900 border-white/10" data-testid="bible-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-800 border-white/10 text-slate-100">
                    <SelectItem value="character">{t("bible.type.character")}</SelectItem>
                    <SelectItem value="world">{t("bible.type.world")}</SelectItem>
                    <SelectItem value="note">{t("bible.type.note")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.name")}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-ink-900 border-white/10 focus-visible:ring-gold" data-testid="bible-name-input" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.role")}</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.traits")}</Label>
              <Input value={form.traits} onChange={(e) => setForm({ ...form, traits: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.relationships")}</Label>
              <Input value={form.relationships} onChange={(e) => setForm({ ...form, relationships: e.target.value })} className="bg-ink-900 border-white/10 focus-visible:ring-gold" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-slate-400">{t("bible.notes")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="bg-ink-900 border-white/10 focus-visible:ring-gold resize-none" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="text-slate-400">{t("modal.cancel")}</Button>
              <Button type="submit" className="bg-gold hover:bg-gold-light text-ink-900 font-semibold" data-testid="bible-save-btn">{t("modal.save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
